import * as React from 'react';
import { Link } from 'react-router-dom';
import { pb } from '@/lib/pb';
import { COLLECTIONS, isAdmin } from '@/lib/types';
import { Icon } from '@/ui';
import { useAuth } from './AuthContext';
import { Card, PageHeader } from './ui';

interface Counts { posts: number; published: number; categories: number; contacts: number | null; }

export function Dashboard() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [counts, setCounts] = React.useState<Counts | null>(null);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      const total = async (col: string, filter?: string): Promise<number> => {
        const r = await pb.collection(col).getList(1, 1, filter ? { filter } : undefined);
        return r.totalItems;
      };
      try {
        const posts = await total(COLLECTIONS.posts);
        const published = await total(COLLECTIONS.posts, 'status = "published"');
        const categories = await total(COLLECTIONS.categories);
        const contacts = admin ? await total(COLLECTIONS.contacts) : null;
        if (alive) setCounts({ posts, published, categories, contacts });
      } catch {
        if (alive) setCounts({ posts: 0, published: 0, categories: 0, contacts: null });
      }
    };
    void load();
    return () => { alive = false; };
  }, [admin]);

  const tiles: { label: string; value: number | null; icon: string; to: string }[] = [
    { label: 'Beiträge', value: counts?.posts ?? null, icon: 'newspaper', to: '/admin/blog' },
    { label: 'Veröffentlicht', value: counts?.published ?? null, icon: 'check-circle', to: '/admin/blog' },
    { label: 'Kategorien', value: counts?.categories ?? null, icon: 'tags', to: '/admin/categories' },
    ...(admin ? [{ label: 'Kontaktanfragen', value: counts?.contacts ?? null, icon: 'mail', to: '/admin/settings' }] : []),
  ];

  return (
    <div>
      <PageHeader title={`Willkommen, ${user?.name || user?.email || ''}`} subtitle="Übersicht über Inhalte und Anfragen." />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        {tiles.map((t) => (
          <Link key={t.label} to={t.to} style={{ textDecoration: 'none' }}>
            <Card style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--text-200)', fontWeight: 500 }}>{t.label}</span>
                <span style={{ color: 'var(--primary-200)', display: 'inline-flex' }}><Icon name={t.icon} size={18} /></span>
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--text-100)', marginTop: 10, letterSpacing: '-0.02em' }}>
                {t.value === null ? '–' : t.value}
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <Card>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-100)', margin: '0 0 14px' }}>Schnellzugriff</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <QuickLink to="/admin/blog/new" icon="plus" label="Neuer Beitrag" />
          <QuickLink to="/admin/blog" icon="newspaper" label="Beiträge verwalten" />
          <QuickLink to="/admin/categories" icon="tags" label="Kategorien" />
          {admin && <QuickLink to="/admin/users" icon="users" label="Benutzer" />}
          {admin && <QuickLink to="/admin/settings" icon="settings" label="Einstellungen" />}
        </div>
      </Card>
    </div>
  );
}

function QuickLink({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    <Link to={to} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border-default)', background: 'var(--surface-page)', color: 'var(--text-100)', fontSize: 13.5, fontWeight: 500, textDecoration: 'none' }}>
      <Icon name={icon} size={16} /> {label}
    </Link>
  );
}
